// client/src/components/media/MediaEditor.jsx
// NATIVE DEVICE EDITOR VERSION
// Uses the device's built-in image editor (Photos app, Gallery, etc.)
// Much simpler and more reliable than embedding a web editor

import React, { useState, useRef } from 'react';

/**
 * Upload edited image blob to server
 */
async function uploadEditedBlob({ blob, filename = 'edited.jpg', tenantId, uploadUrl = '/api/media/upload' }) {
  const fd = new FormData();
  fd.append('file', new File([blob], filename, { type: blob.type || 'image/jpeg' }));
  if (tenantId) fd.append('tenantId', String(tenantId));
  
  const res = await fetch(uploadUrl, { 
    method: 'POST', 
    body: fd, 
    credentials: 'include' 
  });
  
  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Upload failed: ${res.status} ${errorText}`);
  }
  
  const json = await res.json();
  
  if (!json.url) {
    throw new Error('Upload response missing URL');
  }
  
  return json.url;
}

/**
 * MediaEditor Component - Native Device Editor Version
 * Opens a file picker so user can edit with their device's native photo editor
 */
export default function MediaEditor({ 
  src, 
  tenantId, 
  onClose, 
  onSaved, 
  uploadUrl = '/api/media/upload',
  eventId = null,
  itemId = null
}) {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('instructions'); // 'instructions', 'uploading', 'complete'

  /**
   * Handle file selection after editing
   */
  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setBusy(true);
      setError(null);
      setStep('uploading');

      // Generate filename with context
      const timestamp = Date.now();
      const contextPart = itemId 
        ? `item${itemId}` 
        : eventId 
          ? `event${eventId}` 
          : 'media';
      const filename = `edited_${contextPart}_${timestamp}.jpg`;

      console.log('⬆ Uploading edited image...');
      
      // Upload to server
      const newUrl = await uploadEditedBlob({
        blob: file,
        filename,
        tenantId,
        uploadUrl
      });

      console.log('✓ Upload successful:', newUrl);

      setStep('complete');

      // Call parent callback with new URL
      if (onSaved) {
        onSaved(newUrl);
      }

      // Close editor after brief delay
      setTimeout(() => {
        if (onClose) {
          onClose();
        }
      }, 500);
    } catch (e) {
      console.error('❌ Save failed:', e);
      setError(e.message || 'Upload failed. Please try again.');
      setStep('instructions');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Download image for editing
   */
  const handleDownloadForEdit = async () => {
    try {
      // Create a temporary link to download the image
      const link = document.createElement('a');
      link.href = src;
      link.download = `image-to-edit-${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download failed:', e);
      setError('Failed to download image. Try long-pressing the image above to save it.');
    }
  };

  return (
    <div 
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.95)',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}
    >
      {/* Header */}
      <div 
        style={{
          padding: '12px 16px',
          background: '#01057a',
          color: '#ffffff',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <div style={{ fontSize: '16px', fontWeight: '500' }}>
          Edit Image
        </div>
        
        <button 
          onClick={onClose}
          disabled={busy}
          style={{
            padding: '8px 16px',
            borderRadius: '6px',
            border: 'none',
            background: 'rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            cursor: busy ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: busy ? 0.5 : 1
          }}
        >
          Cancel
        </button>
      </div>

      {/* Content */}
      <div 
        style={{
          flex: 1,
          padding: '24px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#ffffff',
          textAlign: 'center',
          maxWidth: '600px',
          margin: '0 auto',
          width: '100%'
        }}
      >
        {step === 'instructions' && (
          <>
            {/* Preview of original image */}
            <div style={{ marginBottom: '24px', width: '100%', maxWidth: '400px' }}>
              <img 
                src={src} 
                alt="Original" 
                style={{
                  width: '100%',
                  height: 'auto',
                  borderRadius: '8px',
                  border: '2px solid #ff6800'
                }}
              />
            </div>

            <h2 style={{ 
              fontSize: '20px', 
              marginBottom: '12px',
              color: '#ffffff'
            }}>
              Edit with Your Photo App
            </h2>

            <p style={{ 
              fontSize: '14px', 
              marginBottom: '24px',
              color: 'rgba(255, 255, 255, 0.8)',
              lineHeight: '1.6'
            }}>
              Use your device's built-in photo editor for the best editing experience.
            </p>

            {error && (
              <div 
                style={{
                  padding: '12px',
                  marginBottom: '16px',
                  background: 'rgba(239, 68, 68, 0.2)',
                  color: '#ef4444',
                  borderRadius: '6px',
                  fontSize: '14px',
                  width: '100%'
                }}
              >
                {error}
              </div>
            )}

            {/* Instructions */}
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '8px',
              padding: '20px',
              marginBottom: '24px',
              textAlign: 'left',
              width: '100%'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#ff6800'
                }}>
                  📱 On Mobile:
                </div>
                <ol style={{ 
                  margin: '0', 
                  paddingLeft: '20px',
                  fontSize: '14px',
                  lineHeight: '1.8'
                }}>
                  <li>Long-press the image above</li>
                  <li>Select "Save Image" or "Download Image"</li>
                  <li>Open your Photos/Gallery app</li>
                  <li>Edit the image with your photo editor</li>
                  <li>Come back here and upload the edited version below</li>
                </ol>
              </div>

              <div>
                <div style={{ 
                  fontSize: '16px', 
                  fontWeight: '500',
                  marginBottom: '8px',
                  color: '#ff6800'
                }}>
                  💻 On Desktop:
                </div>
                <ol style={{ 
                  margin: '0', 
                  paddingLeft: '20px',
                  fontSize: '14px',
                  lineHeight: '1.8'
                }}>
                  <li>Click "Download Image" button below</li>
                  <li>Open the image in your favorite photo editor</li>
                  <li>Edit and save the image</li>
                  <li>Come back here and upload the edited version below</li>
                </ol>
              </div>
            </div>

            {/* Download button */}
            <button
              onClick={handleDownloadForEdit}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '14px',
                marginBottom: '16px',
                background: '#3d3d38',
                color: '#ffffff',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              💾 Download Image
            </button>

            {/* File input for edited image */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileSelected}
              style={{ display: 'none' }}
            />

            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={busy}
              style={{
                width: '100%',
                maxWidth: '300px',
                padding: '14px',
                background: '#ff6800',
                color: '#ffffff',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: busy ? 'not-allowed' : 'pointer',
                opacity: busy ? 0.5 : 1
              }}
            >
              📤 Upload Edited Image
            </button>

            <p style={{ 
              fontSize: '12px', 
              marginTop: '16px',
              color: 'rgba(255, 255, 255, 0.6)'
            }}>
              Supported formats: JPG, PNG, WebP
            </p>
          </>
        )}

        {step === 'uploading' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid rgba(255, 104, 0, 0.3)',
              borderTop: '4px solid #ff6800',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginBottom: '24px'
            }}></div>
            <h2 style={{ fontSize: '20px', marginBottom: '8px' }}>
              Uploading...
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
              Please wait while we save your edited image
            </p>
          </>
        )}

        {step === 'complete' && (
          <>
            <div style={{
              width: '60px',
              height: '60px',
              background: '#10b981',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '30px',
              marginBottom: '24px'
            }}>
              ✓
            </div>
            <h2 style={{ fontSize: '20px', marginBottom: '8px' }}>
              Success!
            </h2>
            <p style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
              Your edited image has been saved
            </p>
          </>
        )}
      </div>

      {/* Add keyframes for spinner */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}